// Dependencies
import React, { useRef, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getVidFleetEvent } from "Core/data/VideoEvents";
import { Box, Grid, Typography, Card, CardContent, Icon, Divider, Tabs, Tab } from "@mui/material";

// Components
import VidFleetVideo from "Modules/.common/components/VidFleetVideo";
import SmartWitnessSnapshot from "Modules/.common/components/SmartWitnessSnapshot";
import Carousel from "react-material-ui-carousel";
import { MenuComponent } from "Components/Menu";
import { Button, Tooltip, LinearProgress } from "Components";
import { Pill } from "@gpstrackit/gtc-react-controls";
import { HasPermission } from "Core/security";
import { TagsGroup } from "Components/Tag";
import { getTagColor } from "Core/data/Helpers";

//actions
import {
  openSharedMediaModal,
  openEventAddNotesModal,
  updateAvailableMedia,
  restoreAvailableMedia,
  // openVideoAdvancedFilters,
  clearRedux,

} from "Redux/actions";

import { openVideoAssignDriverModal } from "../../../../../../.common/components/VideoAssignDriver/redux/slice";
import WaylensPlayerModal from "./WaylensPlayerModal";

import {
  getFormattedDateByTimeZone,
  speedFormatMPH,
  copyToClipboard,
} from "Core/data/Helpers";

import { getLabel } from "Modules/video/.globals/helpers";
import "./index.scss";
import { useIntl } from "react-intl";
import colors from "Assets/sass/core/vars.scss";
import { Timezone } from "Components/uiControls";
import { openModalChangeStatus } from "Modules/video/modals/changeStatus/redux/slice";
import { openModalDeleteVideo } from "Modules/video/modals/delete/redux/slice";
import VideocamIcon from "@mui/icons-material/Videocam";
import { getLightMetricEvent } from "../../../../../../../core/data/VideoEvents";
import Modal, { ModalBody, ModalFooter } from 'Components/Modal';
import { MODE_ENUM } from "../../../../../../.common/components/VidFleetVideo/constants";
import { NotificationManager } from "Components/Notifications";

const DEFAULT_IMAGE_PATH_EN = "/assets/images/no_video_available.png";
const DEFAULT_IMAGE_PATH_ES = "/assets/images/no_video_available_es.png";
// P3-53907: how long to show a loading state for an expired LightMetrics URL before
// falling back to "video not available" — confirmed with the user, no documented SLA
// for how long the real refresh (UpdateLightMetrics step function) takes today.
const EXPIRED_VIDEO_TIMEOUT_MS = 60000;

const detailsFontSize = "14px";
const detailsLineHeight = "19px";
const labelStyle = {
  color: colors.color_disabled_gray,
  fontSize: detailsFontSize,
  lineHeight: detailsLineHeight,
  textAlign: "right",
  whiteSpace: "nowrap",
};
const valueStyle = {
  fontSize: detailsFontSize,
  lineHeight: detailsLineHeight,
  fontWeight: "400",
  color: colors.black,
};

export const getVideoStates = (messages) => {
  const reviewStates = {
    Unreviewed: {
      id: "Unreviewed",
      label: messages[`videoMainView_unreviewed`],
      color: colors["warning"],
    },
    Accepted: {
      id: "Accepted",
      label: messages[`videoMainView_accepted`],
      color: colors["green"],
    },
    Disputed: {
      id: "Disputed",
      label: messages[`videoMainView_disputed`],
      color: colors["red"],
    },
    Reviewed: {
      id: "Reviewed",
      label: messages["videoMainView_reviewed"],
      color: colors["brown"],
    },
    FalsePositive: {
      id: "FalsePositive",
      label: messages["videoMainView_falsePositive"],
      color: colors["purple"],
    },
  };

  return reviewStates;
};

const MITAC_MODELS = ["Mitac-Gemini", "Mitac", "Mitac-Sprint"];

export const ChooseVideo = (props) => {
  const {
    deviceModelName,
    url,
    urls,
    thumbnailUrl,
    deviceData,
    vidFleetVideoRef,
    options,
    urlExpiration,
    mediaType,
  } = props;

  const { locale, messages } = useIntl();
  const [open, setOpen] = useState(false);
  const [activeCameraIndex, setActiveCameraIndex] = useState(0);

  const isVideoExpired = urlExpiration && new Date(urlExpiration) < new Date();
  const defaultImagePath = locale === "en" ? DEFAULT_IMAGE_PATH_EN : DEFAULT_IMAGE_PATH_ES;

  // P3-53907 feedback: an expired URL used to show "video not available" immediately,
  // even though a refresh is normally already in flight (see UpdateLightMetrics.ts +
  // the updateEventsData push-notification bridge) — most of the time it resolves
  // within seconds. Show a loading state instead, and only fall back to the "not
  // available" image if EXPIRED_VIDEO_TIMEOUT_MS passes with no fresh URL — a real
  // failure looks the same as before, just after a grace period instead of instantly.
  const [expiredTimedOut, setExpiredTimedOut] = useState(false);
  useEffect(() => {
    setExpiredTimedOut(false);
    if (!isVideoExpired) return;
    const timer = setTimeout(() => setExpiredTimedOut(true), EXPIRED_VIDEO_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [urlExpiration]);

  if(MITAC_MODELS.includes(deviceModelName)) {
    // Mitac can have multiple cameras; `urls` (one per camera) is the source of truth
    // when present — `url` alone may point at the wrong camera (e.g. an IR-tinted one).
    const videoUrls = urls?.length ? urls : (url ? [url] : []);

    if(!videoUrls.length && !urlExpiration) {
      return (
        <div style={{ cursor: 'not-allowed', background: '#e5e5e5', display: 'flex', justifyContent: 'center', alignItems: 'center', width: "100%", height: "100%" }}>
          <img style={{ width: "160px"}} src={defaultImagePath} alt="Video Expired" /> 
        </div>
      )
    }

    if(isVideoExpired) {
      if (!expiredTimedOut) {
        return (
          <div style={{ cursor: 'progress', background: '#e5e5e5', display: 'flex', justifyContent: 'center', alignItems: 'center', width: "100%", height: "100%" }}>
            <Box width="160px">
              <LinearProgress />
            </Box>
          </div>
        )
      }
      return (
        <div style={{ cursor: 'not-allowed', background: '#e5e5e5', display: 'flex', justifyContent: 'center', alignItems: 'center', width: "100%", height: "100%" }}>
          <img style={{ width: "160px"}} src={defaultImagePath} alt="Video Expired" />
        </div>
      )
    }

    if(mediaType === "picture") {
      return (
        <>
          <Box display="flex" alignItems="center" height="240px">
            <img style={{ width: '100%'}} src={url} alt="Video" onClick={() => setOpen(true)} />
          </Box>
          {
            open && (
              <div style={{position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
                <Modal
                  open={open}
                  position="center"
                  size='full-screen'
                  onClose={() => setOpen(false)}
                  style={{backgroundColor: 'transparent', border: 'none', boxShadow: 'none'}}
                >
                    <ModalBody style={{
                        backgroundColor: 'transparent',
                        border: 'none',
                        boxShadow: 'none',
                      }}>
                      <img src={url} alt="Image" />
                    </ModalBody>
                  </Modal>
              </div>
            )
          }
       
        </>
      )
    }
    
    if (videoUrls.length > 1) {
      const tabsHeight = 28;
      return (
        <Box style={{ width: "96%", marginLeft: "2%", height: "100%", display: "flex", flexDirection: "column" }}>
          <Tabs
            value={activeCameraIndex}
            onChange={(event, newValue) => setActiveCameraIndex(newValue)}
            TabIndicatorProps={{ style: { display: "none" } }}
            sx={{ minHeight: tabsHeight, backgroundColor: "rgba(0,0,0,0.85)" }}
          >
            {videoUrls.map((videoUrl, index) => (
              <Tab
                key={index}
                icon={<VideocamIcon style={{ fontSize: 14 }} />}
                iconPosition="start"
                label={index + 1}
                sx={{
                  minHeight: tabsHeight,
                  minWidth: 44,
                  padding: "2px 8px",
                  color: "grey.500",
                  "&.Mui-selected": { color: "white" },
                }}
              />
            ))}
          </Tabs>
          <Carousel
            fullHeightHover={false}
            autoPlay={false}
            indicators={false}
            index={activeCameraIndex}
            onChange={(newIndex) => setActiveCameraIndex(newIndex)}
            animation="slide"
            height={240 - tabsHeight}
          >
            {videoUrls.map((videoUrl, index) => (
              <video
                key={index}
                controls
                style={{ width: "100%", height: "100%", position: "relative" }}
              >
                <source src={videoUrl} type="video/mp4" />
              </video>
            ))}
          </Carousel>
        </Box>
      )
    }

    return (
      <video
          controls
          style={{
            width: "96%",
            marginLeft: "2%",
            height: "100%",
            position: "relative",
          }}
        >
          <source src={videoUrls[0]} type="video/mp4" />
        </video>
    )
  }

  return (
    <>
      {deviceModelName === "SmartWitness" ? (
        <SmartWitnessSnapshot
          ref={vidFleetVideoRef}
          response={{ thumbnailUrl: thumbnailUrl || url }}
          vehicleTrail={{ deviceData }}
          indicators={false}
          height={240}
        />
      ) : thumbnailUrl || url ? (
        <VidFleetVideo ref={vidFleetVideoRef} {...options} />
      ) : (
        <Box
          style={{
            backgroundColor: "gray",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            height: "100%",
          }}
        >
          <Tooltip title={messages["videoMainView_pending"]}>
            <VideocamIcon
              style={{ color: "white", fontSize: 40, cursor: "progress" }}
            />
          </Tooltip>
        </Box>
      )}
    </>
  );
};


const EventCard = (props) => {
  const vidFleetVideoRef = useRef();
  const dispatch = useDispatch();

  const { messages, locale } = useIntl();

  const { loadingUpdateAvailableMedia, updateAvailableMediaResponse } =
    useSelector((state) => state.videoDetailRedux);

  const { user } = useSelector((state) => state.securityRedux);
  const { isKph } = user;
  const { row, isDeleted, callback, tab, openSliderVideo } = props;
  const { dateformat } = user;

  const reviewStates = getVideoStates(messages);
  const {
    deviceId,
    label,
    eventName,
    unitTime,
    timeZone,
    deviceModelName,
    id,
    driverName,
    starred,
    speed,
    ecuSpeed,
    thumbnailUrl,
    clipID,
    notes,
    eventCode,
    unitType,
    useEcuSpeed,
    duration,
    deviceData,
    tags,
    url,
    urls,
    esn,
    mediaDeletedByUsername,
    reviewStatus
  } = row;

  const [localStarred, setLocalStarred] = useState(starred);
  const [waylensModalOpen, setWaylensModalOpen] = useState(false);
  const deviceDataObject = JSON.parse(deviceData);

  const { urlExpiration, mediaType } = deviceDataObject;

  useEffect(() => {
    if (updateAvailableMediaResponse[id]) {
      if (tab === 2 && callback) callback(row);
      dispatch(
        clearRedux("VIDEO_DETAIL", {
          updateAvailableMediaResponse: {
            ...updateAvailableMediaResponse,
            [id]: null,
          },
          loadingUpdateAvailableMedia: {
            ...loadingUpdateAvailableMedia,
            [id]: false,
          },
        })
      );
    }
  }, [updateAvailableMediaResponse[id]]);

  const isSnapshot = mediaType === "picture" || eventName === "AutoSnapshot" ;

  const contextualCardMenuConfig = [];

  if (!isDeleted && HasPermission(user, ["dtlvdo"], [282]))
    contextualCardMenuConfig.push(messages["videoMainView_delete"]);
  if (!isDeleted)
    contextualCardMenuConfig.push(messages["videoMainView_share"]);
  if (isSnapshot && !isDeleted)
    contextualCardMenuConfig.push(messages["videoMainView_requestClip"]);
  if (isDeleted)
    contextualCardMenuConfig.push(messages["videoMainView_restore"]);
  if (!isDeleted)
    contextualCardMenuConfig.push(messages["videoMainView_changeStatus"]);
  if (HasPermission(user, ["vassdrv"], [283]))
    contextualCardMenuConfig.push(messages["videoMainView_assign"]);

  const options = {
    messages: messages, //For to access to language keys
    mode: isSnapshot ? MODE_ENUM.picture : MODE_ENUM.video,
    type: "playback", //Depends of the playing type, the url VidFleet API changes
    id: clipID || esn, // This could be a ESN or Clip Id
    autoplay: false, //If is true the video will be played automatically
    deviceModel:
      MITAC_MODELS.includes(deviceModelName) ? "4K" : deviceModelName || "4K", //4K|360
    url: url || null,
    thumbnailUrl: thumbnailUrl || url,
    // thumbnailUrl:  thumbnailUrl|| url || locale === "en" ? DEFAULT_IMAGE_PATH_EN : DEFAULT_IMAGE_PATH_ES, // For the available media there is always an available resource
    pictureId:  null, // The id of the picture for request to VidFleet API for the thumbnail
    name: `${clipID || esn}-${id}-${getFormattedDateByTimeZone(
      unitTime,
      timeZone?.name,
      messages,
      "YYYY-MM-DD-HH-mm-ss"
    )}`,
  };

  let event = MITAC_MODELS.includes(deviceModelName) ? getLightMetricEvent(eventCode) : getVidFleetEvent(eventCode);

  const eventLabel = `${
    messages[event?.lanKey] ||
    (eventName === "Crash" ? messages["videoMainView_drivingHit"] : eventName)
  }`;

  if (isSnapshot) {
    event = { ...getVidFleetEvent("AutoSnapshot"), lanKey: event?.lanKey };
  }

  const getSpeed = () => {
    const newSpeed = useEcuSpeed ? (ecuSpeed ? ecuSpeed : speed) : speed;
    const speedFormat = speedFormatMPH(newSpeed, unitType, isKph, false, false);
    return speedFormat;
  };

  const starredAction = () => {
    dispatch(
      updateAvailableMedia({
        id,
        notes: notes,
        deviceId: deviceId,
        starred: !localStarred,
        hideTableLoading: true,
        mediaDeleted: isDeleted,
      })
    );
    setLocalStarred(!localStarred);
  };

  return (
    <>
      <Card
        className={`${
          HasPermission(user, ["vidrw"], [261])
            ? `gtc-video-${reviewStatus?.status?.toLowerCase() || "unreviewed"}`
            : ""
        }`}
        style={{ borderRadius: "20px", overflow: "hidden" }}
      >
        <Grid
          item
          style={{
            display: "flex",
            justifyContent: "center",
            backgroundColor: "black",
          }}
        >
          <div className="ctn-vid-fleet-video vidFleet-video-container">
            <ChooseVideo
              urlExpiration={urlExpiration}
              deviceModelName={deviceModelName}
              url={url}
              urls={urls}
              thumbnailUrl={thumbnailUrl}
              deviceData={deviceData}
              vidFleetVideoRef={vidFleetVideoRef}
              mediaType={mediaType}
              options={options}
            />
          </div>
        </Grid>

        <CardContent
          style={{
            height: isDeleted ? 220 : 220,
            maxHeight: 220,
            position: "relative",
            paddingBottom: 48,
          }}
        >
          <Grid
            container
            direction="column"
            justifyContent="space-between"
            alignItems="flex-start"
            style={{ display: "flex", flexDirection: "column" }}
          >
            <Box
              wrap="nowrap"
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
              }}
            >
              <Grid>
                <img
                  src={event?.icon}
                  alt={event?.name}
                  style={{ display: "block", width: 35, height: 35 }}
                />
              </Grid>

              <Grid item xs zeroMinWidth style={{ paddingRight: 30 }}>
                <Button
                  type="link"
                  style={{ color: colors.black, fontWeight: 500, fontSize: "18px", marginLeft: 16 }}
                  url={`/app/video/videoDetail/media/${id}?deviceId=${deviceId}`}
                  onClick={() =>
                    dispatch(
                      clearRedux("AVAILABLE_MEDIA", { availableMedia: [] })
                    )
                  }
                >
                  <Tooltip key={"event"} title={eventLabel}>
                    <Typography noWrap style={{ fontSize: 18, fontWeight: 500 }}>
                      {eventLabel}
                    </Typography>
                  </Tooltip>
                </Button>
              </Grid>
            </Box>

            <Divider style={{ margin: "10px 0", width: "100%" }} />

            <Grid style={{ display: "flex", justifyContent: "start" }}>
              <Box
                sx={{
                  width: "110%",
                  display: "grid",
                  gridTemplateColumns: "max-content 1fr",
                  columnGap: "12px",
                  rowGap: "4px",
                  alignItems: "start",
                  fontSize: "15px",
                  justifyContent: "center",
                }}
              >
                <Typography sx={labelStyle}>{messages["videoMainView_when"]} :</Typography>
                <Box sx={{ minWidth: 0 }}>
                  <Timezone
                    date={unitTime}
                    dateFormat={dateformat}
                    timeFormat="hh:mm a"
                    timezone={timeZone?.name}
                    className="gtc-video-date-time"
                  />
                </Box>

                <Typography sx={labelStyle}>{messages["videoClipRequest_driver"]} :</Typography>
                <Box sx={{ minWidth: 0 }}>
                  <Tooltip title={driverName || messages["videoClipRequest_unassigned"]} placement="bottom">
                    <Typography noWrap sx={valueStyle}>
                      {driverName || messages["videoClipRequest_unassigned"]}
                    </Typography>
                  </Tooltip>
                </Box>

                <Typography sx={labelStyle}>{messages["videoClipRequest_unit"]} :</Typography>
                <Box sx={{ minWidth: 0 }}>
                  <Tooltip title={label || messages["videoClipRequest_unassigned"]} placement="bottom">
                    <Typography noWrap sx={valueStyle}>
                      {label || messages["videoClipRequest_unassigned"]}
                    </Typography>
                  </Tooltip>
                </Box>

                <Typography sx={labelStyle}>{messages["videoMainView_speed"] || "Speed"} :</Typography>
                <Box sx={{ minWidth: 0 }}>
                  <Typography noWrap sx={valueStyle}>{getSpeed() || ""}</Typography>
                </Box>

                <Typography sx={labelStyle}>{messages["videoMainView_tags"]} :</Typography>
                <Box sx={{ minWidth: 0, marginTop: tags?.length === 1 ? "0px" : "10px" }}>
                  <TagsGroup
                    options={
                      tags?.length
                        ? tags?.map((t) => ({ ...t, color: getTagColor(t?.color) }))
                        : []
                    }
                    nameField="label"
                    colorField="color"
                  />
                </Box>

                <Typography sx={labelStyle}>{messages["videoMainView_eventId"]} :</Typography>
                <Box sx={{ minWidth: 0 }}>
                  <div className="gtc-video-event-id-container">
                    <Tooltip title={`${deviceId || ""}-${id || ""}`} placement={"bottom"}>
                      <Typography noWrap sx={{ ...valueStyle, paddingRight: 5 }}>
                        {deviceId || ""}-{id || ""}
                      </Typography>
                    </Tooltip>
                    <Button
                      type="icon"
                      icon="content_copy"
                      onClick={() => copyToClipboard(`${deviceId || ""}-${id || ""}`)}
                      disabled={loadingUpdateAvailableMedia[id]}
                      fontSize="small"
                      style={{ fontSize: 15 }}
                      className="btn-no-padding"
                    />
                  </div>
                </Box>

                {isDeleted && mediaDeletedByUsername && (
                  <>
                    <Typography sx={labelStyle}>{messages["videoMainView_deletedBy"]} :</Typography>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography noWrap sx={valueStyle}>
                        {mediaDeletedByUsername}
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>
            </Grid>

            <Box
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                position: "absolute",
                width: "100%",
                bottom: "5px",
                paddingLeft: 0,
                paddingRight: "15px",
              }}
            >
              <Box style={{ marginTop: 8 }}>
                {HasPermission(user, ["vidrw"], [261]) && (
                  <Tooltip
                    title={reviewStatus?.description}
                    placement="bottom"
                  >
                    <div>
                      <Pill
                        textField={"label"}
                        item={
                          reviewStates[reviewStatus?.status] ||
                          reviewStates["Unreviewed"]
                        }
                        variant="filled"
                      />
                    </div>
                  </Tooltip>
                )}
              </Box>

              <Box style={{ display: "flex", margin: "0px 15px 0 0" }}>
                {reviewStatus?.description && reviewStatus?.description !== "undefined" && reviewStatus?.description !== "null" && (
                  <Tooltip title={reviewStatus?.description || notes} placement={"bottom"}>
                    <div style={{ padding: 7 }}>
                      <Icon name="notes" fontSize="small">
                        notes
                      </Icon>
                    </div>
                  </Tooltip>
                )}

                {!isDeleted && clipID && deviceModelName !== 'SmartWitness' && !MITAC_MODELS.includes(deviceModelName) && (
                  <Button
                    type="icon"
                    icon="play_circle_outline"
                    tooltip={messages['videoMainView_waylensPlayer'] || 'Enriched Video'}
                    onClick={() => setWaylensModalOpen(true)}
                    disabled={loadingUpdateAvailableMedia[id]}
                  />
                )}

                {!isDeleted && (
                  <Button
                    type="icon"
                    icon={localStarred ? "star" : "star_outline"}
                    tooltip={
                      localStarred
                        ? messages["videoMainView_removeStarred"]
                        : messages["videoMainView_addStarred"]
                    }
                    onClick={() => starredAction()}
                    disabled={loadingUpdateAvailableMedia[id]}
                  />
                )}

                {deviceModelName != "360" && (
                  <Button
                    type="icon"
                    icon="get_app"
                    tooltip={
                      isDeleted || deviceModelName === "360"
                        ? messages["videoMainView_download360Error"]
                        : messages["download"]
                    }
                    disabled={isDeleted || deviceModelName === "360"}
                    onClick={(e) => {
                      if (e) e.preventDefault();
                      if (vidFleetVideoRef?.current) {
                        if (options.mode === "video") {
                          vidFleetVideoRef?.current.downloadVideo();
                        } else if (options.mode === "picture") {
                          vidFleetVideoRef?.current.downloadPicture();
                        }
                        return;
                      }
                      // Bug fix: LightMetrics/Mitac devices render plain <video> tags
                      // (see ChooseVideo's MITAC_MODELS branch) instead of VidFleetVideo,
                      // so vidFleetVideoRef is never attached for them — the button did
                      // nothing at all before this fix.
                      //
                      // Fallback only, not a real one-click download: LightMetrics' S3
                      // bucket (pxstagingdatabucketlm) is vendor-owned, not ours, and
                      // doesn't allow cross-origin reads (no CORS), so the blob-based
                      // download() helper VidFleetVideo uses internally fails with a
                      // CORS error for these URLs specifically — confirmed against a
                      // real staging URL. Opening in a new tab at least lets the user
                      // save the file manually (right-click -> save as). A real one-click
                      // download needs either a backend proxy or LightMetrics enabling
                      // CORS on their bucket — both bigger than this fix.
                      const downloadUrls = urls?.length ? urls : (url ? [url] : []);
                      downloadUrls.forEach((downloadUrl) => {
                        window.open(downloadUrl, "_blank", "noopener,noreferrer");
                      });
                    }}
                  />
                )}

                <Grid item xs>
                  <MenuComponent
                          disabled={loadingUpdateAvailableMedia[id]}
                          options={contextualCardMenuConfig}
                          icon={"more_vert"}
                          className="icon-menu-vehicle-service showInHover"
                          // eslint-disable-next-line no-unused-vars
                          onClick={(type, value) => {
                            switch (type) {
                              case messages["videoMainView_addNote"]:
                                dispatch(
                                  openVideoAdvancedFilters(false, "Events")
                                );
                                dispatch(
                                  openEventAddNotesModal({
                                    open: true,
                                    data: {
                                      id,
                                      deviceId,
                                      notes,
                                      label: getLabel(messages, row),
                                    },
                                  })
                                );
                                break;
                              case messages["videoMainView_share"]:
                                dispatch(
                                  openSharedMediaModal({
                                    open: true,
                                    items: [
                                      {
                                        entity: {
                                          id: id,
                                          name: "DeviceReading",
                                          label: getLabel(messages, row),
                                        },
                                        deviceId: deviceId,
                                      },
                                    ],
                                  })
                                );
                                break;
                              case messages["videoMainView_delete"]:
                                dispatch(
                                  openModalDeleteVideo({
                                    type: "media",
                                    items: [
                                      {
                                        label: getLabel(messages, row, locale),
                                        id: { rowId: id, deviceId },
                                      },
                                    ],
                                  })
                                );
                                break;
                              case messages["videoMainView_restore"]:
                                dispatch(
                                  restoreAvailableMedia([{ id, deviceId }]),
                                  //notification success
                                  NotificationManager.success(
                                    messages["videoMainView_restoreSuccess"],
                                    messages["success"],
                                    3000,
                                    null,
                                    null,
                                    "success"
                                  )
                                );
                                callback && callback(row);
                                break;

                              case messages["videoMainView_assign"]:
                                dispatch(openVideoAssignDriverModal(row));
                                break;

                              case messages["videoMainView_requestClip"]:
                                openSliderVideo(row);
                                break;
                              case messages["videoMainView_changeStatus"]:
                                //dispatch(openModalEventChangeStatus({open: true, data: [row] }))
                                dispatch(
                                  openModalChangeStatus({ items: [row] })
                                );
                                break;
                              default:
                                break;
                            }
                          }}
                          value={row}
                          visible
                        />
                      </Grid>
                    </Box>
            </Box>
          </Grid>
        </CardContent>
        {loadingUpdateAvailableMedia[id] && <LinearProgress color="primary" />}
      </Card>

      <WaylensPlayerModal
        open={waylensModalOpen}
        onClose={() => setWaylensModalOpen(false)}
        clipId={clipID}
      />
    </>
  );
};

export default EventCard;
